import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Calendar, Sun, Moon, ChevronDown, BookOpen, Users, Heart, Sparkles } from 'lucide-react'
import { useBooking } from '../contexts/BookingContext'

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const [isClassesDropdownOpen, setIsClassesDropdownOpen] = useState(false)
  const [isMobileClassesOpen, setIsMobileClassesOpen] = useState(false)
  const location = useLocation()
  const { setIsDemo, setIsBookingOpen, setSelectedClass } = useBooking()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme === 'dark') {
      setIsDark(true)
      document.documentElement.classList.add('dark')
    } else {
      setIsDark(false)
      document.documentElement.classList.remove('dark')
    }
  }, [])

  // Close mobile menu when clicking outside or on escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        const target = event.target as Element
        if (!target.closest('nav')) {
          closeMobileMenu()
        }
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeMobileMenu()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const toggleTheme = () => {
    const newTheme = !isDark
    setIsDark(newTheme)
    if (newTheme) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  const navigationItems = [
    { label: 'Home', href: '/' },
    { 
      label: 'Classes', 
      href: '/classes',
      hasDropdown: true,
      dropdownItems: [
        { label: 'All Classes', href: '/classes', icon: <BookOpen className="w-4 h-4" /> },
        { label: 'Special Gems: Dance and Movement Therapy', href: '/classes/1', icon: <Heart className="w-4 h-4" /> },
        { label: 'Bharatanatyam & Semi-Classical', href: '/classes/2', icon: <Sparkles className="w-4 h-4" /> },
        { label: 'Lazy Group Dance for Senior Citizens', href: '/classes/3', icon: <Users className="w-4 h-4" /> },
        { label: 'Zumba and Yoga Fitness', href: '/classes/4', icon: <Heart className="w-4 h-4" /> },
        { label: 'Events and Choreography', href: '/classes/5', icon: <Sparkles className="w-4 h-4" /> },
        { label: 'Art and Craft Workshops', href: '/classes/6', icon: <BookOpen className="w-4 h-4" /> },
      ]
    },
    { label: 'Gallery', href: '/gallery' },
    { label: 'About Us', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ]

  const handleBookingClick = (isDemo = false) => {
    setSelectedClass(null) // No specific class selected for general booking
    setIsDemo(isDemo)
    setIsBookingOpen(true)
    setIsOpen(false)
    setIsMobileClassesOpen(false)
  }

  const closeMobileMenu = () => {
    setIsOpen(false)
    setIsMobileClassesOpen(false)
  }

  const handleHomeClick = (e: React.MouseEvent) => {
    // If already on home page, scroll to top
    if (location.pathname === '/') {
      e.preventDefault()
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-200/20 dark:border-gray-700/20' 
          : 'bg-white dark:bg-gray-900'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" onClick={handleHomeClick} className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">DS</span>
            </div>
            <div>
              <h1 className="text-xl font-bold gradient-text dark:text-white">
                Dance with Dr. Sophie
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Professional Dance Studio</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <div key={item.label} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => {
                      if (item.label === 'Classes') setIsClassesDropdownOpen(true)
                    }}
                    onMouseLeave={() => {
                      if (item.label === 'Classes') setIsClassesDropdownOpen(false)
                    }}
                  >
                    <button
                      className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                        isActiveRoute(item.href)
                          ? 'text-primary-600 bg-primary-50 dark:bg-primary-700/20'
                          : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-300'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </button>

                    <AnimatePresence>
                      {item.label === 'Classes' && isClassesDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                        >
                          {item.dropdownItems?.map((dropdownItem, index) => (
                            <Link
                              key={index}
                              to={dropdownItem.href}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                            >
                              <span className="mr-3">{dropdownItem.icon}</span>
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    onClick={item.label === 'Home' ? handleHomeClick : undefined}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                      isActiveRoute(item.href)
                        ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-300'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            </button>

            {/* Book Class Button */}
            <button
              onClick={() => handleBookingClick(false)}
              className="hidden sm:flex btn btn-primary"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book a Class
            </button>

            {/* Mobile Menu */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative z-50"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <motion.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 overflow-hidden relative z-50 w-full"
          >
            <div className="px-4 py-6 space-y-2">
              {navigationItems.map((item) => (
                <div key={item.label}>
                  {item.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => setIsMobileClassesOpen(!isMobileClassesOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <span>{item.label}</span>
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isMobileClassesOpen ? 'rotate-180' : ''
                          }`} 
                        />
                      </button>
                      <AnimatePresence>
                        {isMobileClassesOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="ml-4 mt-2 space-y-1"
                          >
                            {item.dropdownItems?.map((dropdownItem, index) => (
                              <Link
                                key={index}
                                to={dropdownItem.href}
                                onClick={closeMobileMenu}
                                className={`flex items-center px-4 py-2 rounded-lg text-sm transition-colors ${
                                  isActiveRoute(dropdownItem.href)
                                    ? 'text-primary-600 bg-primary-50 dark:bg-primary-700/20'
                                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                              >
                                <span className="mr-3">{dropdownItem.icon}</span>
                                {dropdownItem.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={(e) => {
                        if (item.label === 'Home') {
                          handleHomeClick(e)
                        }
                        closeMobileMenu()
                      }}
                      className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                        isActiveRoute(item.href)
                          ? 'text-primary-600 bg-primary-50 dark:bg-primary-700/20'
                          : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile Action Buttons */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <button
                  onClick={() => handleBookingClick(false)}
                  className="w-full btn btn-primary text-center"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a Class
                </button>
                <button
                  onClick={() => handleBookingClick(true)}
                  className="w-full btn btn-outline text-center"
                >
                  Try Free Demo
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}